import {
  DragDropEvents,
  DragDropManager,
  Draggable,
  Droppable,
  UniqueIdentifier
} from '@dnd-kit/abstract';
import { arrayMove, arraySwap } from '@dnd-kit/helpers';

type Items = UniqueIdentifier[] | { id: UniqueIdentifier }[];

export function reorder<
  T extends Items | Record<UniqueIdentifier, Items>,
  U extends Draggable,
  V extends Droppable,
  W extends DragDropManager<U, V>
>(
  items: T,
  event: Parameters<DragDropEvents<U, V, W>['dragover'] | DragDropEvents<U, V, W>['dragend']>[0],
  mutation: typeof arrayMove | typeof arraySwap
): T {
  const { source, target, canceled } = event.operation;

  if (!source || !target || canceled) {
    if ('preventDefault' in event) event.preventDefault();
    return items;
  }

  const findIndex = (item: Items[0], id: UniqueIdentifier) =>
    item === id || (typeof item === 'object' && 'id' in item && item.id === id);

  // ----- Flat list -----
  if (Array.isArray(items)) {
    const sourceIndex = items.findIndex(item => findIndex(item, source.id));
    const targetIndex = items.findIndex(item => findIndex(item, target.id));

    if (sourceIndex === -1 || targetIndex === -1) {
      return items;
    }

    // Reconcile optimistic updates
    if (!canceled && 'index' in source && typeof source.index === 'number') {
      const projectedSourceIndex = source.index;
      if (projectedSourceIndex !== sourceIndex) {
        return mutation(items, sourceIndex, projectedSourceIndex);
      }
    }

    return mutation(items, sourceIndex, targetIndex);
  }

  // ----- Nested (columns -> items) -----
  const entries = Object.entries(items);

  let sourceIndex = -1;
  let sourceParent: UniqueIdentifier | undefined;
  let targetIndex = -1;
  let targetParent: UniqueIdentifier | undefined;

  for (const [id, children] of entries) {
    if (sourceIndex === -1) {
      sourceIndex = children.findIndex(item => findIndex(item, source.id));
      if (sourceIndex !== -1) {
        sourceParent = id;
      }
    }

    if (targetIndex === -1) {
      targetIndex = children.findIndex(item => findIndex(item, target.id));
      if (targetIndex !== -1) {
        targetParent = id;
      }
    }

    if (sourceIndex !== -1 && targetIndex !== -1) {
      break;
    }
  }

  if (!source.manager) return items;

  const { dragOperation } = source.manager;
  const position = dragOperation.shape?.current.center ?? dragOperation.position.current;

  // If hovering a container (not its children), treat it as valid target and append
  if (targetParent == null) {
    if ((target.id as any) in items) {
      targetParent = target.id;
      targetIndex = items[targetParent].length; // 👈 append
    }
  }

  if (
    sourceParent == null ||
    targetParent == null ||
    (sourceParent === targetParent && sourceIndex === targetIndex)
  ) {
    if ('preventDefault' in event) event.preventDefault();
    return items;
  }

  // Same parent: reorder/swap within the column
  if (sourceParent === targetParent) {
    return {
      ...items,
      [sourceParent]: mutation(items[sourceParent], sourceIndex, targetIndex)
    } as T;
  }

  // Different parents:
  // If we didn't land on a specific child in the target, default to appending at end
  if (targetIndex === -1) {
    targetIndex = items[targetParent].length;
  }

  // Only use below/above modifier if we actually hovered a specific child index
  let modifier = 0;
  const hasConcreteChildTarget = targetIndex >= 0 && targetIndex < items[targetParent].length;

  if (hasConcreteChildTarget && target.shape) {
    const isBelowTarget = Math.round(position.y) > Math.round(target.shape.center.y);
    modifier = isBelowTarget ? 1 : 0;
  }

  const sourceItem = items[sourceParent][sourceIndex];

  return {
    ...items,
    [sourceParent]: [
      ...items[sourceParent].slice(0, sourceIndex),
      ...items[sourceParent].slice(sourceIndex + 1)
    ],
    [targetParent]: [
      ...items[targetParent].slice(0, targetIndex + modifier),
      sourceItem,
      ...items[targetParent].slice(targetIndex + modifier)
    ]
  } as T;
}
